import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import GiftCard from "@/components/GiftCard";
import api, { formatGBP } from "@/lib/api";
import { Search, SlidersHorizontal } from "lucide-react";

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// coarser steps at the expensive end; the last stop means no upper limit at all
const PRICE_STOPS = [
  ...Array.from({ length: 17 }, (_, i) => 20 + i * 5),       // £20–£100 in £5
  125, 150, 175, 200, 225, 250, 275, 300,                     // £25 steps
  350, 400, 450, 500,                                         // £50 steps
  600, 700, 800, 900, 1000,                                   // £100 steps
  null,                                                       // £1,000+ — no limit
];
const NO_LIMIT_INDEX = PRICE_STOPS.length - 1;

export default function Browse() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState(params.get("category") || "all");
  const [priceIdx, setPriceIdx] = useState(NO_LIMIT_INDEX);
  const [loading, setLoading] = useState(true);
  const maxPrice = PRICE_STOPS[priceIdx];

  useEffect(() => {
    api.get("/products/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const cat = category === "all" ? "" : category;
    const params = { q, category: cat };
    if (maxPrice != null) params.max_price = maxPrice;
    api.get("/products", { params })
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [q, category, maxPrice]);

  const catName = (slug) => categories.find((c) => c.slug === slug)?.name || cap(String(slug || "").replace(/-/g, " "));

  const recs = useMemo(
    () => products.map((p) => ({ product: p, label: catName(p.category), match: null })),
    [products, categories]
  );

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-10 py-8 lg:py-12 gm-fade">
      <div className="mb-8 gm-fade-up">
        <span className="font-mono text-xs uppercase tracking-widest text-brand-amber font-semibold">The catalogue</span>
        <h1 className="font-serif text-3xl lg:text-5xl mt-2">Browse gift ideas</h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">
          Explore our curated UK gift catalogue, or <button onClick={() => navigate("/quiz")} className="text-brand-green underline underline-offset-2 hover:text-brand-amber">take the quiz</button> for tailored picks with reasons.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-brand-cream2 p-5 mb-8 flex flex-col lg:flex-row gap-5 lg:items-center">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="browse-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search gifts, brands, interests…" className="pl-9" />
        </div>
        <div className="w-full lg:w-52">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="browse-category-select"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-full lg:w-64">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="flex items-center gap-1.5 text-muted-foreground"><SlidersHorizontal className="w-3.5 h-3.5" /> Max price</span>
            <span className="font-mono font-semibold" data-testid="browse-price-label">
              {maxPrice == null ? "£1,000+" : formatGBP(maxPrice)}
            </span>
          </div>
          <Slider data-testid="browse-price-slider" value={[priceIdx]} onValueChange={(v) => setPriceIdx(v[0])}
            min={0} max={NO_LIMIT_INDEX} step={1} className="[&_[role=slider]]:bg-brand-green [&>span>span]:bg-brand-green" />
        </div>
      </div>

      <p className="text-sm text-muted-foreground mb-4">{loading ? "Loading…" : `${recs.length} gifts`}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
        {recs.map((r, i) => (
          <GiftCard key={r.product.product_id} rec={r} index={i} sessionId="browse" showWhy={false} />
        ))}
      </div>

      {!loading && recs.length === 0 && (
        <div className="text-center py-20 text-muted-foreground">No gifts match those filters.</div>
      )}
    </div>
  );
}
